import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import { VerificationReviewApi } from '@/modules/tenants/services/verification-api';
import type {
  DecideVerificationCaseRequest,
  VerificationCaseReview,
  VerificationDecision,
  VerificationReasonCode,
} from '@/api/schemas/vendor-verification';
import { FiSearch, FiCheckCircle, FiXCircle, FiInfo } from 'react-icons/fi';

const DECISION_OPTIONS = [
  { value: 'approved', label: 'Approve' },
  { value: 'rejected', label: 'Reject' },
  { value: 'more_evidence_required', label: 'Request more evidence' },
];

const REASON_OPTIONS = [
  { value: 'evidence_incomplete', label: 'Evidence incomplete' },
  { value: 'evidence_invalid', label: 'Evidence invalid' },
  { value: 'evidence_expired', label: 'Evidence expired' },
  { value: 'subject_mismatch', label: 'Subject mismatch' },
  { value: 'authority_not_demonstrated', label: 'Authority not demonstrated' },
];

const STATUS_TONE: Record<VerificationCaseReview['status'], 'positive' | 'caution' | 'critical' | 'neutral' | 'info'> = {
  draft: 'neutral',
  submitted: 'info',
  decided: 'positive',
  expired: 'critical',
};

/**
 * Platform document-verification review (M08).
 *
 * A platform verifier is NOT a member of the tenant being reviewed, so the tenant
 * is named with the `X-Tenant-Id` SELECTOR header. The verifier projection
 * releases the three account identifiers the vendor projection withholds, and
 * each read writes one `audit_log` row.
 *
 * The case is looked up by its `caseId`; the operator pastes it from the vendor's
 * submission. Separation of duties is enforced server-side: the submitter cannot
 * decide their own case.
 */
export function VerificationReviewPanel({ tenantId }: { tenantId: string }): ReactNode {
  const [caseId, setCaseId] = useState('');
  const [caseData, setCaseData] = useState<VerificationCaseReview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const lookup = async () => {
    setIsLoading(true);
    setError(null);
    setCaseData(null);
    try {
      setCaseData(await VerificationReviewApi.getReview(tenantId, caseId.trim()));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load verification case'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3">
        <Input
          label="Verification case ID"
          hint="The UUID of the case to review."
          value={caseId}
          onChange={(event) => setCaseId(event.currentTarget.value)}
        />
        <Button onClick={() => void lookup()} loading={isLoading} disabled={caseId.trim() === ''}>
          <FiSearch className="mr-2 h-4 w-4" />
          Load case
        </Button>
      </div>

      {error ? <ErrorState message={error.message} /> : null}

      {caseData ? (
        <CaseDetail
          tenantId={tenantId}
          caseData={caseData}
          onUpdated={(updated) => setCaseData(updated)}
        />
      ) : null}
    </div>
  );
}

function CaseDetail({
  tenantId,
  caseData,
  onUpdated,
}: {
  tenantId: string;
  caseData: VerificationCaseReview;
  onUpdated: (caseData: VerificationCaseReview) => void;
}): ReactNode {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h4 className="font-medium">
            {caseData.type} · {caseData.subjectKind}
          </h4>
          <p className="text-xs text-content-muted">
            Subject {caseData.subjectId} · version {caseData.version}
          </p>
        </div>
        <Badge tone={STATUS_TONE[caseData.status]}>{caseData.status}</Badge>
      </div>

      <CaseFields caseData={caseData} />
      <EvidenceList evidence={caseData.evidence} />

      {caseData.status === 'submitted' ? (
        <DecisionForm tenantId={tenantId} caseData={caseData} onUpdated={onUpdated} />
      ) : null}
    </div>
  );
}

function CaseFields({ caseData }: { caseData: VerificationCaseReview }): ReactNode {
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Decision" value={caseData.decision ?? '—'} />
      <Field label="Reason code" value={caseData.reasonCode ?? '—'} />
      <Field label="Submitted by" value={caseData.submittedByAccountId ?? '—'} />
      <Field label="Decided by" value={caseData.decidedByAccountId ?? '—'} />
      <Field
        label="Submitted at"
        value={caseData.submittedAt ? new Date(caseData.submittedAt).toLocaleString() : '—'}
      />
      <Field
        label="Decided at"
        value={caseData.decidedAt ? new Date(caseData.decidedAt).toLocaleString() : '—'}
      />
    </dl>
  );
}

function EvidenceList({ evidence }: { evidence: VerificationCaseReview['evidence'] }): ReactNode {
  return (
    <div>
      <h4 className="mb-3 font-medium">Evidence ({evidence.length})</h4>
      {evidence.length === 0 ? (
        <p className="text-sm text-content-muted">No evidence attached.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {evidence.map((item) => (
            <li key={item.evidenceId} className="px-4 py-3">
              <p className="text-sm text-content">Document {item.documentId}</p>
              <p className="text-xs text-content-muted">
                Attached by {item.attachedByAccountId} ·{' '}
                {new Date(item.attachedAt).toLocaleString()}
                {item.validTo ? ` · valid to ${new Date(item.validTo).toLocaleDateString()}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DecisionForm({
  tenantId,
  caseData,
  onUpdated,
}: {
  tenantId: string;
  caseData: VerificationCaseReview;
  onUpdated: (caseData: VerificationCaseReview) => void;
}): ReactNode {
  const [decision, setDecision] = useState<VerificationDecision>('approved');
  const [reasonCode, setReasonCode] = useState<VerificationReasonCode>('evidence_incomplete');
  const [expiresAt, setExpiresAt] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const input = buildDecisionInput(decision, reasonCode, expiresAt);
      const updated = await VerificationReviewApi.decide(
        tenantId,
        caseData.caseId,
        caseData.version,
        input,
      );
      // The decision response is the VENDOR projection; re-read the verifier
      // projection so the released account identifiers stay visible.
      onUpdated({ ...caseData, ...updated, evidence: caseData.evidence });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to record decision'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <h4 className="font-medium">Record decision</h4>
      <DecisionFields
        decision={decision}
        reasonCode={reasonCode}
        expiresAt={expiresAt}
        onDecision={setDecision}
        onReasonCode={setReasonCode}
        onExpiresAt={setExpiresAt}
      />
      {error ? <ErrorState message={error.message} /> : null}
      <Button onClick={() => void submit()} loading={pending}>
        <DecisionIcon decision={decision} />
        Record decision
      </Button>
    </div>
  );
}

function DecisionFields({
  decision,
  reasonCode,
  expiresAt,
  onDecision,
  onReasonCode,
  onExpiresAt,
}: {
  decision: VerificationDecision;
  reasonCode: VerificationReasonCode;
  expiresAt: string;
  onDecision: (value: VerificationDecision) => void;
  onReasonCode: (value: VerificationReasonCode) => void;
  onExpiresAt: (value: string) => void;
}): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Select
        label="Decision"
        options={DECISION_OPTIONS}
        value={decision}
        onChange={(event) => onDecision(event.target.value as VerificationDecision)}
      />
      {decision !== 'approved' ? (
        <Select
          label="Reason code"
          options={REASON_OPTIONS}
          value={reasonCode}
          onChange={(event) => onReasonCode(event.target.value as VerificationReasonCode)}
        />
      ) : (
        <Input
          label="Expires at"
          type="date"
          hint="Optional. The date the verification lapses."
          value={expiresAt}
          onChange={(event) => onExpiresAt(event.currentTarget.value)}
        />
      )}
    </div>
  );
}

function DecisionIcon({ decision }: { decision: VerificationDecision }): ReactNode {
  if (decision === 'approved') return <FiCheckCircle className="mr-2 h-4 w-4" />;
  if (decision === 'rejected') return <FiXCircle className="mr-2 h-4 w-4" />;
  return <FiInfo className="mr-2 h-4 w-4" />;
}

function buildDecisionInput(
  decision: VerificationDecision,
  reasonCode: VerificationReasonCode,
  expiresAt: string,
): DecideVerificationCaseRequest {
  const input: DecideVerificationCaseRequest = { decision };
  if (decision !== 'approved') input.reasonCode = reasonCode;
  if (decision === 'approved' && expiresAt.trim() !== '') {
    input.expiresAt = new Date(expiresAt).toISOString();
  }
  return input;
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}
